// useQueriesBySource Hook
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  QueryEntity,
  QueryFilters,
  QueryPagination,
  QuerySourcePayload,
} from "../../domain/entities/QueryEntity";
import { GetQueriesBySourceUseCase } from "../../domain/use-cases/GetQueriesBySourceUseCase";
import { QueriesRepositoryImpl } from "../../data/repositories/QueriesRepositoryImpl";
import { queriesApi } from "../../data/api/queriesApi";

const DEFAULT_LIMIT = 5;

const createEmptyPagination = (): QueryPagination => ({
  limit: DEFAULT_LIMIT,
  offset: 0,
  hasMore: false,
  total: 0,
});

export const useQueriesBySource = (
  source: string,
  sourceId?: number,
  filters?: QueryFilters
) => {
  const [queries, setQueries] = useState<QueryEntity[]>([]);
  const [pagination, setPagination] = useState<QueryPagination>(
    createEmptyPagination()
  );
  const [sourceInfo, setSourceInfo] = useState<QuerySourcePayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const repository = useMemo(() => new QueriesRepositoryImpl(queriesApi), []);

  const getQueriesUseCase = useMemo(
    () => new GetQueriesBySourceUseCase(repository),
    [repository]
  );

  const fetchQueries = useCallback(
    async (offset = 0, append = false) => {
      if (!source || sourceId == null) {
        setQueries([]);
        setPagination(createEmptyPagination());
        setSourceInfo(null);
        setError(null);
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
        return;
      }

      setError(null);
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const result = await getQueriesUseCase.execute(
          source,
          sourceId,
          filters,
          DEFAULT_LIMIT,
          offset
        );

        if (result.isSuccess) {
          setQueries((prev) =>
            append ? [...prev, ...result.value.queries] : result.value.queries
          );
          setPagination(result.value.pagination);
          setSourceInfo(result.value.source ?? null);
        } else {
          if (!append) {
            setQueries([]);
            setPagination(createEmptyPagination());
            setSourceInfo(null);
          }
          setError(result.error);
        }
      } catch (err: any) {
        if (!append) {
          setQueries([]);
          setPagination(createEmptyPagination());
          setSourceInfo(null);
        }
        setError(err.message || "Failed to fetch queries");
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [source, sourceId, filters, getQueriesUseCase]
  );

  const hasMore = pagination.hasMore;

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) {
      return;
    }

    const limit = Math.max(pagination.limit, DEFAULT_LIMIT);
    const nextOffset = pagination.offset + limit;
    fetchQueries(nextOffset, true);
  }, [fetchQueries, hasMore, loading, loadingMore, pagination]);

  const refetch = useCallback(() => {
    fetchQueries(0, false);
  }, [fetchQueries]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    queries,
    loading,
    loadingMore,
    error,
    refetch,
    pagination,
    source: sourceInfo,
    hasMore,
    loadMore,
  };
};
